import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Folder, FileText, 
  ChevronRight, Save, X, Calendar, Clock, ExternalLink, Upload, Image, Loader2
} from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import AdminLayout from './AdminLayout';

const AdminBlog = () => {
  const { adminToken } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('articles');
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [modalType, setModalType] = useState('category'); // category or article
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState({ status: '', category: '' });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchCategories();
    fetchArticles();
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [filter]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/categories`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/blog/admin/articles?limit=50`;
      if (filter.status) url += `&status=${filter.status}`;
      if (filter.category) url += `&category=${filter.category}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      if (data.success) {
        setArticles(data.data);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCategoryModal = (category = null) => {
    setModalType('category');
    setEditingItem(category);
    setFormData(category ? { name: category.name, description: category.description || '' } : { name: '', description: '' });
    setShowModal(true);
  };

  const openArticleModal = async (article = null) => {
    setModalType('article');
    setEditingItem(article);
    
    if (article) {
      // Fetch full article with content
      try {
        const response = await fetch(`${API_URL}/api/blog/admin/articles/${article.id}`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        const data = await response.json();
        if (data.success) {
          setFormData({
            title: data.data.title,
            category_id: data.data.category_id,
            content: data.data.content,
            excerpt: data.data.excerpt || '',
            cover_image: data.data.cover_image || '',
            meta_description: data.data.meta_description || '',
            status: data.data.status
          });
        }
      } catch (error) {
        console.error('Error fetching article:', error);
      }
    } else {
      setFormData({
        title: '',
        category_id: categories[0]?.id || '',
        content: '',
        excerpt: '',
        cover_image: '',
        meta_description: '',
        status: 'draft'
      });
    }
    setShowModal(true);
  };

  const saveCategory = async () => {
    setSaving(true);
    try {
      const url = editingItem 
        ? `${API_URL}/api/blog/admin/categories/${editingItem.id}`
        : `${API_URL}/api/blog/admin/categories`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        fetchCategories();
      } else {
        alert(data.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const saveArticle = async () => {
    if (!formData.title || !formData.category_id || !formData.content) {
      alert('Compila tutti i campi obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      const url = editingItem 
        ? `${API_URL}/api/blog/admin/articles/${editingItem.id}`
        : `${API_URL}/api/blog/admin/articles`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        fetchArticles();
      } else {
        alert(data.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Upload immagine su S3
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Verifica tipo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Tipo file non supportato. Usa: JPG, PNG, WebP, GIF');
      return;
    }
    
    // Verifica dimensione (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File troppo grande. Massimo 5MB');
      return;
    }
    
    setUploading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await fetch(`${API_URL}/api/blog/admin/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        },
        body: formDataUpload
      });
      
      const data = await response.json();
      
      if (data.success && data.url) {
        setFormData(prev => ({ ...prev, cover_image: data.url }));
      } else {
        alert(data.detail || 'Errore nel caricamento');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Errore nel caricamento immagine');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deleteCategory = async (category) => {
    if (!window.confirm(`Eliminare la categoria "${category.name}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/categories/${category.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      if (data.success) {
        fetchCategories();
      } else {
        alert(data.detail || 'Errore nell\'eliminazione');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const deleteArticle = async (article) => {
    if (!window.confirm(`Eliminare l'articolo "${article.title}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/articles/${article.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      if (data.success) {
        fetchArticles();
      } else {
        alert(data.detail || 'Errore nell\'eliminazione');
      }
    } catch (error) {
      console.error('Error deleting article:', error);
    }
  };

  const toggleArticleStatus = async (article) => {
    const newStatus = article.status === 'published' ? 'draft' : 'published';
    
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/articles/${article.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        fetchArticles();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestione Blog</h1>
            <p className="text-gray-600">Gestisci categorie e articoli del blog</p>
          </div>
        </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b">
        <button
          onClick={() => setActiveTab('articles')}
          className={`pb-3 px-1 font-medium flex items-center gap-2 border-b-2 transition-all ${
            activeTab === 'articles'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileText className="w-5 h-5" />
          Articoli ({articles.length})
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          className={`pb-3 px-1 font-medium flex items-center gap-2 border-b-2 transition-all ${
            activeTab === 'categories'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Folder className="w-5 h-5" />
          Categorie ({categories.length})
        </button>
      </div>

      {/* Articles Tab */}
      {activeTab === 'articles' && (
        <div>
          {/* Filters & Actions */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex gap-3">
              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Tutti gli stati</option>
                <option value="published">Pubblicati</option>
                <option value="draft">Bozze</option>
              </select>
              <select
                value={filter.category}
                onChange={(e) => setFilter({ ...filter, category: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Tutte le categorie</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => openArticleModal()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Nuovo Articolo
            </button>
          </div>

          {/* Articles Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titolo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      Caricamento...
                    </td>
                  </tr>
                ) : articles.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      Nessun articolo trovato
                    </td>
                  </tr>
                ) : (
                  articles.map((article) => (
                    <tr key={article.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {article.cover_image ? (
                            <div className="w-12 h-12 rounded-lg overflow-hidden">
                              <img 
                                src={article.cover_image} 
                                alt="" 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                              <FileText className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{article.title}</p>
                            <p className="text-sm text-gray-500">/blog/{article.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded">
                          {article.category_name}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleArticleStatus(article)}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-sm font-medium ${
                            article.status === 'published'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {article.status === 'published' ? (
                            <><Eye className="w-4 h-4" /> Pubblicato</>
                          ) : (
                            <><EyeOff className="w-4 h-4" /> Bozza</>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(article.published_at || article.created_at)}
                        </div>
                        <div className="flex items-center gap-1 text-xs mt-1">
                          <Clock className="w-3 h-3" />
                          {article.reading_time} min
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {article.status === 'published' && (
                            <a
                              href={`/blog/${article.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Visualizza"
                            >
                              <ExternalLink className="w-5 h-5" />
                            </a>
                          )}
                          <button
                            onClick={() => openArticleModal(article)}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Modifica"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => deleteArticle(article)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div>
          <div className="flex justify-end mb-6">
            <button
              onClick={() => openCategoryModal()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Nuova Categoria
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <div key={category.id} className="bg-white rounded-xl shadow-sm p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Folder className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openCategoryModal(category)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteCategory(category)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{category.name}</h3>
                {category.description && (
                  <p className="text-sm text-gray-500 mb-3">{category.description}</p>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <FileText className="w-4 h-4" />
                  {category.article_count} articoli
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-2xl shadow-2xl w-full ${modalType === 'article' ? 'max-w-4xl max-h-[90vh] overflow-y-auto' : 'max-w-md'}`}>
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {modalType === 'category' 
                  ? (editingItem ? 'Modifica Categoria' : 'Nuova Categoria')
                  : (editingItem ? 'Modifica Articolo' : 'Nuovo Articolo')
                }
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {modalType === 'category' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Es: Guide e Tutorial"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="3"
                      placeholder="Descrizione opzionale della categoria"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
                      <input
                        type="text"
                        value={formData.title || ''}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Es: Come creare un amaro personalizzato"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                      <select
                        value={formData.category_id || ''}
                        onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Seleziona categoria</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
                      <select
                        value={formData.status || 'draft'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="draft">Bozza</option>
                        <option value="published">Pubblicato</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Immagine di copertina</label>
                    
                    {/* Preview immagine */}
                    {formData.cover_image && (
                      <div className="mb-3 relative inline-block">
                        <div className="w-32 h-32 rounded-lg shadow-sm border overflow-hidden">
                          <img 
                            src={formData.cover_image} 
                            alt="Preview" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, cover_image: '' })}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    {/* Upload e URL */}
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Caricamento...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Carica immagine
                          </>
                        )}
                      </button>
                      <span className="text-gray-400 self-center">oppure</span>
                      <input
                        type="text"
                        value={formData.cover_image || ''}
                        onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Incolla URL immagine..."
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Formati supportati: JPG, PNG, WebP, GIF (max 5MB)</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contenuto *</label>
                    <p className="text-xs text-gray-500 mb-2">Puoi usare HTML per formattare il testo (es: &lt;h2&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt;)</p>
                    <textarea
                      value={formData.content || ''}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                      rows="12"
                      placeholder="<p>Scrivi qui il contenuto dell'articolo...</p>"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estratto (anteprima)</label>
                    <textarea
                      value={formData.excerpt || ''}
                      onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="2"
                      placeholder="Breve descrizione mostrata nelle anteprime (auto-generato se vuoto)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description (SEO)</label>
                    <textarea
                      value={formData.meta_description || ''}
                      onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="2"
                      placeholder="Descrizione per i motori di ricerca (max 160 caratteri)"
                      maxLength={160}
                    />
                    <p className="text-xs text-gray-500 mt-1">{(formData.meta_description || '').length}/160 caratteri</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={modalType === 'category' ? saveCategory : saveArticle}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default AdminBlog;
